import { useState, type KeyboardEvent } from 'react';
import { useFjernDatapeker, useLagreDatapeker, useVarsleMeg, type DataPointer } from '../api/useHypoteseHukommelse.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { kanFoelges } from './kilder.ts';
import { hvem, navnAv } from './lesning.ts';
import { kortDatoAv } from './setning.ts';

/**
 * «Har du noe til denne?» (H3.3, konseptsiden W14, W15, M6): «Jeg har data på dette» og «Varsle
 * meg» på en hypotese, for alle i rommet.
 *
 * - Bidragene står med navn for alle i rommet (produkteier 2026-10-29), eldste først, med et rått
 *   tall — aldri sortert per person. Ett per person, og bare før konklusjonen.
 * - «Varsle meg» finnes på alle åpne hypoteser. Antallet som følger finnes ikke i kontrakten og
 *   vises aldri (Kh8). Varselet står i «Til deg» ved hver statusendring et menneske gjør, til
 *   personen trykker «OK» — lesing tar det aldri ut (Kh9).
 */

const FOER_KONKLUSJON = ['mottatt', 'vurderes', 'parkert', 'valgt', 'testes', 'pa_pause'];
export const kanGiData = (h: Pick<Hypothesis, 'status'>): boolean => FOER_KONKLUSJON.includes(h.status);

const MND_LANG = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const langDato = (iso: string) => `${Number(iso.slice(8, 10))}. ${MND_LANG[Number(iso.slice(5, 7)) - 1] ?? ''}`;

/** Setningen som erstatter knappen når personen følger (W14). */
export function varselSetning(h: Pick<Hypothesis, 'status' | 'revisitAt'>): string {
  return `Du får den i Til deg når noen gjør noe med den${h.status === 'parkert' && h.revisitAt ? ` — også når den tas opp igjen ${langDato(h.revisitAt)}` : ''}.`;
}

export function Interesse({
  h,
  megId,
  tittelTag: T,
  knapperIFoten,
  onGiData,
}: {
  h: Hypothesis;
  megId: string | undefined;
  tittelTag: 'h2' | 'h3';
  /** Under 900 står de to valgene i foten (M6); setningen og lista står her. */
  knapperIFoten: boolean;
  onGiData: () => void;
}) {
  const varsle = useVarsleMeg();
  const fjern = useFjernDatapeker();
  const pekere = h.dataPointers ?? [];
  const min = pekere.find((d) => d.by.id === megId);
  const data = kanGiData(h);
  const foelge = kanFoelges(h.statusType, h.status);
  const visSeksjon = data || foelge;
  return (
    <>
      {visSeksjon ? (
        <section className="hi-seksjon" aria-labelledby="hi-h" data-testid="hb-interesse">
          <T id="hi-h" className="hi-tittel">
            Har du noe til denne?
          </T>
          {!knapperIFoten && data && !min ? (
            <div>
              <button type="button" className="panel-knapp-sekundaer" onClick={onGiData} data-testid="hi-data">
                <Ikon navn="bar-chart-outline" />
                Jeg har data på dette
              </button>
            </div>
          ) : null}
          {foelge ? (
            h.watching ? (
              <p className="hi-varsel">
                <Ikon navn="notifications-outline" />
                <span>{varselSetning(h)}</span>
                <button type="button" className="panel-knapp-lenke hb-noeytral" onClick={() => varsle.mutate({ id: h.id, paa: false })}>
                  Slutt å varsle
                </button>
              </p>
            ) : !knapperIFoten ? (
              <div>
                <button type="button" className="panel-knapp-sekundaer" aria-busy={varsle.isPending || undefined} onClick={() => varsle.mutate({ id: h.id, paa: true })}>
                  <Ikon navn="notifications-outline" />
                  Varsle meg
                </button>
              </div>
            ) : null
          ) : null}
          {varsle.isError ? <p className="hb-varsel">Fikk ikke lagret varselet. Prøv igjen.</p> : null}
        </section>
      ) : null}
      {pekere.length > 0 ? (
        <section className="hi-data" aria-labelledby="hi-data-h">
          <p id="hi-data-h" className="hi-data-tittel">
            {pekere.length} har data på dette
          </p>
          <ul className="hi-liste" aria-label={`${pekere.length} har data på dette`}>
            {pekere.map((d) => (
              <Peker key={d.id} d={d} megId={megId} kanEndre={data} onEndre={onGiData} onFjern={() => fjern.mutate(h.id)} />
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function Peker({ d, megId, kanEndre, onEndre, onFjern }: { d: DataPointer; megId: string | undefined; kanEndre: boolean; onEndre: () => void; onFjern: () => void }) {
  const egen = d.by.id === megId;
  return (
    <li className="hi-rad">
      <Ikon navn="bar-chart-outline" className="hi-rad-ikon" />
      <span className="hi-rad-tekst">
        <b>{egen ? `${navnAv(d.by)} (deg)` : navnAv(d.by)}</b>
        <span className="hb-tekst">{d.text}</span>
        {d.url ? (
          <a href={d.url} target="_blank" rel="noopener noreferrer nofollow" className="hi-lenke">
            <Ikon navn="link-outline" />
            {d.url.replace(/^https:\/\//, '')}
          </a>
        ) : null}
        {egen && kanEndre ? (
          <span className="hi-egen">
            <button type="button" className="panel-knapp-lenke" onClick={onEndre}>
              Endre
            </button>
            <button type="button" className="panel-knapp-lenke hb-noeytral" onClick={onFjern}>
              Fjern
            </button>
          </span>
        ) : null}
      </span>
      <span className="hb-liten hi-dato">{kortDatoAv(d.at)}</span>
    </li>
  );
}

const LENKE = /^https:\/\/.+$/;

/** W15: én dialog (den lagrer, så DD-47 gjelder) med ett felt som kreves og lenken valgfri. */
export function DataDialog({ h, megId, megNavn, min, onLukk, onLagret }: { h: Hypothesis; megId: string | undefined; megNavn: string | undefined; min: number; onLukk: () => void; onLagret: () => void }) {
  const finnes = (h.dataPointers ?? []).find((d) => d.by.id === megId);
  const [tekst, setTekst] = useState(finnes?.text ?? '');
  const [lenke, setLenke] = useState(finnes?.url ?? '');
  const lagre = useLagreDatapeker();
  const lenkeFeil = lenke.trim() !== '' && !LENKE.test(lenke.trim());
  const grunn = tekst.trim().length < min ? `Skriv hva du har — minst ${min} tegn.` : lenkeFeil ? 'Rett lenken først.' : null;
  const send = () => {
    if (grunn || lagre.isPending) return;
    lagre.mutate({ id: h.id, body: { text: tekst.trim(), ...(lenke.trim() ? { url: lenke.trim() } : {}) } }, { onSuccess: onLagret });
  };
  const paaTast = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    }
  };
  return (
    <Dialog tittelId="hi-dlg-h" onLukk={onLukk} testid="hi-dialog">
      <div className="hi-dialog" onKeyDown={paaTast}>
        <h2 id="hi-dlg-h" className="panel-h2">
          Jeg har data på dette
        </h2>
        <div className="h-felt">
          <label htmlFor="hi-tekst" className="h-etikett">
            Hva har du, og hvor?
          </label>
          <textarea id="hi-tekst" className="h-input h-tekstfelt" rows={4} maxLength={280} value={tekst} onChange={(e) => setTekst(e.target.value)} />
        </div>
        <div className="h-felt">
          <label htmlFor="hi-lenke" className="h-etikett">
            Lenke · valgfri
          </label>
          <input id="hi-lenke" className="h-input" type="url" inputMode="url" placeholder="https://" maxLength={500} value={lenke} aria-invalid={lenkeFeil || undefined} aria-describedby={lenkeFeil ? 'hi-lenke-grunn' : undefined} onChange={(e) => setLenke(e.target.value)} />
          {lenkeFeil ? (
            <span id="hi-lenke-grunn" className="h-felt-grunn">
              <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
              Lenken må begynne med https://.
            </span>
          ) : null}
        </div>
        <p className="h-panel-info">
          <Ikon navn="information-circle-outline" />
          <span>Bidraget vises med navnet ditt, {megNavn ?? hvem({ id: megId ?? '' }, megId, 'du')}, for alle i rommet. Du kan ha ett per hypotese.</span>
        </p>
        {lagre.isError ? (
          <p role="alert" className="hb-varsel">
            <Ikon navn="warning-outline" />
            Fikk ikke lagret. Prøv igjen.
          </p>
        ) : null}
        <div className="panel-handlinger">
          <Knapp label="Avbryt" lenke onClick={onLukk} />
          <Knapp label={finnes ? 'Lagre' : 'Legg til'} kbd="Ctrl+Enter" primaer grunn={grunn} onClick={send} testid="hi-legg-til" />
        </div>
      </div>
    </Dialog>
  );
}
